import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { useSubscriptionPlans, type SubscriptionPlan } from '../../hooks/useSubscriptionPlans';
import Switch from '../form/switch/Switch';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';

interface UpgradePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: string;
  reason?: string;
}

const FEATURE_NAMES: Record<string, string> = {
  rewards: 'Sistema de Recompensas',
  recipes: 'Recetas',
  mealPlans: 'Planificación de Comidas',
  shoppingLists: 'Listas de Compras',
  evaluations: 'Evaluaciones',
  groups: 'Gestión de Grupos',
  activities: 'Actividades',
  checklists: 'Checklists',
  members: 'Miembros',
  aiGenerator: 'Generador con IA',
  aiAssistant: 'Asistente de Crianza',
};

export function UpgradePlanModal({
  isOpen,
  onClose,
  feature,
  reason,
}: UpgradePlanModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { planInfo, isFeatureBlocked } = usePlanLimits();
  const { plans, loading } = useSubscriptionPlans(); // Lee planes REALES de Firestore
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  
  if (!isOpen) return null;

  const currentPlanId = planInfo?.id || 'free';
  const currentPlanPrice = (planInfo as any)?.price || 0;
  
  // Filtrar planes disponibles para upgrade (solo activos y con precio mayor)
  const availablePlans = plans
    .filter(p => p.isActive && p.id !== currentPlanId && p.price > currentPlanPrice)
    .sort((a, b) => a.price - b.price);
  
  const featureName = feature ? (FEATURE_NAMES[feature] || feature) : 'esta función';

  // CRÍTICO: Modal es bloqueante si la feature está bloqueada
  const isBlocking = feature ? isFeatureBlocked(feature as any) : false;

  const handleUpgrade = (planId?: string) => {
    onClose();
    const billing = billingCycle === 'yearly' ? '&billing=yearly' : '';
    navigate(planId ? `/subscription?plan=${planId}${billing}` : `/subscription?billing=${billingCycle}`);
  };
  
  const handleClose = () => {
    if (isBlocking) {
      // BLOQUEANTE: Redirigir a dashboard, NO permitir cerrar
      navigate('/dashboard');
    }
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={isBlocking ? undefined : handleClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-3xl">✨</span>
                <h2 className="text-2xl font-bold text-white">
                  ¡Desbloquea {featureName}!
                </h2>
              </div>
              <p className="text-white/90 text-sm">
                {reason || `Actualiza tu plan para acceder a ${featureName} y muchas más funciones`}
              </p>
            </div>
            {!isBlocking && (
              <button
                onClick={handleClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                aria-label="Cerrar"
              >
                <span className="text-white text-xl">×</span>
              </button>
            )}
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Toggle de facturación */}
          <div className="mb-6 flex items-center justify-center gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-xl">
            <span className={`text-sm font-medium transition-colors ${
              billingCycle === 'monthly' 
                ? 'text-purple-600 dark:text-purple-400' 
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              Mensual
            </span>
            <Switch
              label=""
              checked={billingCycle === 'yearly'}
              onChange={(checked) => setBillingCycle(checked ? 'yearly' : 'monthly')}
            />
            <span className={`text-sm font-medium transition-colors ${
              billingCycle === 'yearly' 
                ? 'text-purple-600 dark:text-purple-400' 
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              Anual
              <span className="ml-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full">
                -17%
              </span>
            </span>
          </div>

          {availablePlans.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400">
                Ya tienes el mejor plan disponible 🎉
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Planes Disponibles
              </h3>
              
              {availablePlans.map((plan) => (
                <PlanCard 
                  key={plan.id} 
                  plan={plan} 
                  billingCycle={billingCycle}
                  onSelect={() => handleUpgrade(plan.id)}
                />
              ))}
            </div>
          )}

          {availablePlans.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => handleUpgrade()}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-semibold shadow-lg shadow-purple-500/30"
              >
                Ver Todos los Planes
              </button>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 text-center">
            <div className="flex items-center justify-center gap-3 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  try {
                    const platform = Capacitor.getPlatform();
                    const isNative = platform === 'android' || platform === 'ios';
                    if (!isNative) {
                      window.open('/terms', '_blank', 'noopener,noreferrer');
                      return;
                    }
                  } catch {
                  }
                  navigate('/terms');
                }}
                className="underline hover:text-gray-700 dark:hover:text-gray-300"
              >
                {t('auth.termsAndConditions')}
              </button>
              <span className="text-gray-400">•</span>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  try {
                    const platform = Capacitor.getPlatform();
                    const isNative = platform === 'android' || platform === 'ios';
                    if (!isNative) {
                      window.open('/privacy', '_blank', 'noopener,noreferrer');
                      return;
                    }
                  } catch {
                  }
                  navigate('/privacy');
                }}
                className="underline hover:text-gray-700 dark:hover:text-gray-300"
              >
                {t('auth.privacyPolicy')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, billingCycle, onSelect }: { plan: SubscriptionPlan; billingCycle: 'monthly' | 'yearly'; onSelect: () => void }) {
  const isRecommended = plan.recommended;
  
  // Calcular precio según ciclo de facturación
  const monthlyPrice = plan.priceMonthly || plan.price;
  const yearlyPrice = plan.priceYearly || (monthlyPrice * 12);
  const yearlyMonthlyEquivalent = yearlyPrice / 12;
  
  const displayPrice = billingCycle === 'yearly' ? yearlyMonthlyEquivalent : monthlyPrice;
  const savings = billingCycle === 'yearly' ? (monthlyPrice * 12 - yearlyPrice) : 0;

  return (
    <div 
      className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${
        isRecommended 
          ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 shadow-lg' 
          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:border-purple-300 dark:hover:border-purple-700'
      }`}
      onClick={onSelect}
    >
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full shadow-md">
            ⭐ Recomendado
          </span>
        </div>
      )}

      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
            {plan.name}
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            {plan.description}
          </p>

          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-3xl font-bold text-gray-900 dark:text-white">
              ${displayPrice.toFixed(2)}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">/ mes</span>
            {billingCycle === 'yearly' && savings > 0 && (
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                (ahorra ${savings.toFixed(2)} al año)
              </span>
            )}
          </div>
          
          {billingCycle === 'yearly' && (
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
              ${yearlyPrice.toFixed(2)} facturados anualmente
            </p>
          )}

          <div className="space-y-2">
            {(() => {
              const feats = (plan.displayFeatures || []) as string[];
              return feats.slice(0, 5).map((feat: string, idx: number) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="text-green-500 text-lg flex-shrink-0">✓</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">{feat}</span>
              </div>
              ));
            })()}
            {(((plan.displayFeatures || []) as string[]).length > 5) && (
              <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                +{(((plan.displayFeatures || []) as string[]).length - 5)} funciones más
              </p>
            )}
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          className={`ml-4 px-4 py-2 rounded-lg font-medium transition-all ${
            isRecommended
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md'
              : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          Seleccionar
        </button>
      </div>
    </div>
  );
}

export default UpgradePlanModal;
